import React, { useState } from 'react';
import PageHeader from '../components/PageHeader';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { GALLERY } from '../mock';

const CATEGORIES = ['All', ...new Set(GALLERY.map((g) => g.category))];

export default function Gallery() {
  const [active, setActive] = useState('All');
  const [selected, setSelected] = useState(null);
  const items = active === 'All' ? GALLERY : GALLERY.filter((g) => g.category === active);

  return (
    <div>
      <Seo title="Gallery | Fixed Group — Reefer Repairs, Fleet & Cold Rooms" description="See Fixed Group in action — reefer container repairs, cold-chain fleet, cold room construction and port terminal operations across Malaysia." />
      <PageHeader title="Gallery" crumbs={[{ label: 'Gallery' }]} />

      <section className="py-16">
        <div className="fx-container">
          <Reveal className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-semibold">Our work in action</span>
            <h2 className="section-title text-3xl md:text-4xl font-extrabold mt-2">Repairs, fleet and cold rooms</h2>
            <p className="mt-6 text-slate-600 leading-relaxed">A look at the operations that keep refrigerated cargo moving — from depot-side PTI and reefer repairs to nationwide cold-chain haulage and custom cold room builds.</p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-colors ${active === c ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-600/25' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300 hover:text-emerald-700'}`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
            {items.map((g, i) => (
              <Reveal key={`${g.title}-${i}`} delay={(i % 3) * 0.08}>
                <button
                  onClick={() => setSelected(g)}
                  data-testid={`gallery-item-${i}`}
                  className="fx-card overflow-hidden group w-full text-left block"
                >
                  <div className="h-60 overflow-hidden relative">
                    <img src={g.image} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-5 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-emerald-600 font-semibold">{g.category}</div>
                      <h3 className="mt-1 font-bold text-slate-900">{g.title}</h3>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-slate-950 border-slate-800" data-testid="gallery-lightbox">
          {selected && (
            <div>
              <img src={selected.image} alt={selected.title} className="w-full max-h-[70vh] object-contain bg-slate-950" />
              <div className="p-5 text-white">
                <div className="text-[11px] uppercase tracking-widest text-emerald-400 font-semibold">{selected.category}</div>
                <DialogTitle className="mt-1 text-lg font-bold text-white">{selected.title}</DialogTitle>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
